<template>
  <Button
    ref="googleLoginButton"
    :variant="'border-back'"
    :disabled="!client || disabled"
    class="google-login-button switch-mode-btn"
    @click="handleClick"
  >
    <template #icon>
      <IconGoogle class="icon-google" />
    </template>
    {{ $t('continue-with-google') }}
  </Button>
</template>

<script lang="ts" setup>
import IconGoogle from '@rendora/design/assets/color/google.svg?component';
import { Button } from '@rendora/design/components/button/index';
import { omit } from 'lodash-es';
import { getUserInternationalLoginAuthorize, postUserInternationalOauth2Login } from '~/api';
import { withRegisterOrLoginChannelForUs } from '~/api/module/open-login';
import { getLocation } from '~/api/module/seo';
import { TRACK_EVENT, trackEvent } from '~/utils/track';

defineProps<{ disabled?: boolean }>();
const emits = defineEmits(['success']);

const googleLoginButton = ref<HTMLElement | null>(null);
const client = ref<any>();

const handleClick = async () => {
  trackEvent(TRACK_EVENT.LOGIN_GOOGLE_CLICK);
  await initialize();
  if (client.value) {
    client.value.requestCode();
  }
};

const loadScript = () => {
  return new Promise<void>(resolve => {
    if (window.google) {
      return resolve();
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
};

const initialize = async () => {
  const [locationRes, googleOauthRes] = await concurrency([
    getLocation(),
    getUserInternationalLoginAuthorize({
      service: 'google',
    }),
    loadScript(),
  ]);

  if (googleOauthRes) {
    client.value = window.google.accounts.oauth2.initCodeClient({
      ...omit(googleOauthRes.data, 'nonce'),
      callback: async (revocationResponse: any) => {
        try {
          const res = await postUserInternationalOauth2Login(
            withRegisterOrLoginChannelForUs({
              code: revocationResponse.code,
              state: revocationResponse.state,
              service: 'google',
              country: locationRes.country,
            }),
          );
          trackEvent(TRACK_EVENT.GOOGLE_LOGIN_SUCCESS);
          emits('success', res.data);
        } catch (err: any) {
          console.error(err);
          // 埋点：Google登录失败
          trackEvent(TRACK_EVENT.LOGIN_GOOGLE_FAIL, {
            error_code: err?.code || err?.error_code || 'unknown',
            error_msg: err?.msg || err?.error_reason || err?.message || '登录失败',
          });
        }
      },
    });
  }
};

onMounted(initialize);
</script>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;
.google-login-button {
  position: relative;
}
.icon-google {
  height: 20px;
  width: 20px;

  @include mobile {
    height: responsive-size(20px);
    width: responsive-size(20px);
  }
}
</style>
